/**
 * Platform detection + Capacitor-aware helpers. Lazy-imports Capacitor only when
 * the runtime indicates we're on a native shell, so web bundles don't pull in
 * iOS plugin code unnecessarily.
 */

import { Capacitor } from '@capacitor/core';
import { getAppSettings } from './app-settings.js';

export type ShareStatus = 'shared' | 'copied' | 'failed' | 'cancelled';

export interface InstalledAppInfo {
  name: string;
  id: string;
  version: string;
  build: string;
  platform: 'web' | 'ios' | 'android';
}

export const WEB_APP_INFO: InstalledAppInfo = {
  name: 'Truebend',
  id: 'com.fiveohninelectric.bender',
  version: '1.0',
  build: 'web',
  platform: 'web',
};

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function getPlatform(): 'web' | 'ios' | 'android' {
  const p = Capacitor.getPlatform();
  if (p === 'ios' || p === 'android') return p;
  return 'web';
}

export async function getInstalledAppInfo(): Promise<InstalledAppInfo> {
  const platform = getPlatform();
  if (!isNative()) return WEB_APP_INFO;

  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    return {
      name: info.name || WEB_APP_INFO.name,
      id: info.id || WEB_APP_INFO.id,
      version: info.version || WEB_APP_INFO.version,
      build: info.build || WEB_APP_INFO.build,
      platform,
    };
  } catch {
    return { ...WEB_APP_INFO, platform };
  }
}

export function formatInstalledAppVersion(info: InstalledAppInfo): string {
  if (!info.build || info.build === 'web') return `${info.version} \u00b7 Web preview`;
  return `${info.version} \u00b7 Build ${info.build}`;
}

/**
 * Trigger a light haptic tap on native; no-op on web.
 * Use sparingly — only on confirmed user actions (mark-complete, submit).
 */
export async function lightHaptic(): Promise<void> {
  if (!getAppSettings().hapticFeedback) return;
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {
    // ignore; haptic is decorative
  }
}

function isShareCancelled(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError',
  );
}

/**
 * Open the iOS share sheet (or web share if available) for a URL.
 * Falls back to copying to clipboard if neither is available.
 */
export async function shareUrl(url: string, title: string): Promise<ShareStatus> {
  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ url, title, dialogTitle: title });
      return 'shared';
    } catch (error) {
      if (isShareCancelled(error)) return 'cancelled';
      // fall through
    }
  }
  if (typeof navigator !== 'undefined' && 'share' in navigator) {
    try {
      await navigator.share({ url, title });
      return 'shared';
    } catch (error) {
      if (isShareCancelled(error)) return 'cancelled';
      // user cancelled or browser blocked
    }
  }
  // Final fallback: clipboard
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}

export async function shareText(text: string, title: string): Promise<ShareStatus> {
  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ text, title, dialogTitle: title });
      return 'shared';
    } catch (error) {
      if (isShareCancelled(error)) return 'cancelled';
      // fall through
    }
  }
  if (typeof navigator !== 'undefined' && 'share' in navigator) {
    try {
      await navigator.share({ text, title });
      return 'shared';
    } catch (error) {
      if (isShareCancelled(error)) return 'cancelled';
      // user cancelled or browser blocked
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
